import { useMutation } from '@tanstack/react-query';
import { List as F7List, ListItem } from 'framework7-react';
import { FC, useMemo, useState } from 'react';

import { queryClient } from '@contexts/queryClient';
import dayjs from 'dayjs';
import mixpanel from 'mixpanel-browser';

import API from '@api/API';

import { FeedEvent, useEventQuery, useUnauthorizedEventsQuery } from '@queries/EventQueries';
import { Series } from '@queries/TagQueries';

import { useSubmitFlyerMutation } from '@mutations/EventMutations';

import { useAnalytics } from '@hooks/useAnalytics';

import Button from '@components/buttons/Button';
import { DatePickerV2 } from '@components/form/DatePicker';
import { ImageUploadField } from '@components/form/ImageUploadField';
import Card from '@components/layout/Card/Card';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { Popup } from '@components/layout/Popup/Popup';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

const AddPosterPopupContent: FC<{
  series: Pick<Series, '_id'>;
  tagSlug: string;
  onClose: () => void;
}> = ({ series, tagSlug, onClose }) => {
  const { track } = useAnalytics();
  const [dateFieldValue, setDateFieldValue] = useState('');
  const [selectedDate, setSelectedDate] = useState('');

  const [selectedEventId, setSelectedEventId] = useState<string>();

  const [isEventConfirmationStepVisible, setIsEventConfirmationStepVisible] = useState(false);

  const [updatedImage, setUpdatedImage] = useState<string | ArrayBuffer>();
  const [startDate, setStartDate] = useState<string>();
  const [startTime, setStartTime] = useState<string>();

  const { isSubmittingFlyer, submitFlyerAsync } = useSubmitFlyerMutation();

  const { eventsAreReady, events = [] } = useUnauthorizedEventsQuery(
    {
      tagIds: [series._id],
      startDate: selectedDate,
      endDate: selectedDate,
      rsvpData: 'ignore',
    },
    Boolean(selectedDate),
  );

  const { eventIsReady, event: selectedEvent } = useEventQuery(selectedEventId);
  const isEventConfirmationStepReady = selectedEventId ? eventIsReady : true;

  const startDateTime = useMemo(() => {
    if (startDate && startTime) {
      return `${startDate} ${startTime}`;
    }

    if (selectedEvent) {
      return `${selectedEvent.startDate} ${selectedEvent.startTime}`;
    }

    return '';
  }, [selectedEvent, startDate, startTime]);

  const isFormDataTouched = useMemo(() => {
    if (selectedEvent) {
      return Boolean(
        updatedImage ||
        startDate !== selectedEvent.startDate ||
        startTime !== selectedEvent.startTime,
      );
    }

    return Boolean(updatedImage || startDate || startTime);
  }, [selectedEvent, updatedImage, startDate, startTime]);

  const handleSubmitFlyer = async () => {
    track('Submit Flyer', {
      selectedEventId,
      hasUpdatedImage: Boolean(updatedImage),
      startDate,
      startTime,
    });

    const distinctId = mixpanel.get_distinct_id();

    const { feedEvent } = await submitFlyerAsync({
      eventId: selectedEvent?._id,
      distinctId,
      base64Image: updatedImage as string,
      startDate,
      startTime,
      seriesId: series._id,
    });

    try {
      const queryClientValue = queryClient.getQueryData(['seriesPostersReviews', tagSlug]);

      const updatedQueryClientValue = {
        events: [feedEvent, ...((queryClientValue as { events: FeedEvent[] })?.events || [])],
      };

      queryClient.setQueryData(['seriesPostersReviews', tagSlug], updatedQueryClientValue);
    } catch (error) {
      console.error('Error updating query client after submitting flyer:', error);
    }

    onClose();
  };

  const isFormReady = selectedEventId ? true : Boolean(startDateTime && updatedImage);

  return (
    <>
      <TitleToolbar
        text={
          isEventConfirmationStepVisible && selectedEvent
            ? 'Confirm Show Details'
            : 'Add a Show Flyer'
        }
        size="md"
      />

      {isEventConfirmationStepVisible ? (
        isEventConfirmationStepReady && (
          <>
            {selectedEvent ? (
              <>
                <p className="mt-0">
                  Glad to know we have your show, but please help us confirm some of the details!
                </p>

                <p>Is this the correct start time? Use the field below to update if needed.</p>
              </>
            ) : (
              <p>Use the field below to update the start time.</p>
            )}

            <DatePickerV2
              name="startDateTime"
              showTimeSelect
              value={startDateTime}
              minDate={dayjs().format('YYYY-MM-DD')}
              onChange={(updatedDate) => {
                setStartDate(dayjs(updatedDate).format('YYYY-MM-DD'));
                setStartTime(dayjs(updatedDate).format('HH:mm'));
              }}
            />

            {selectedEvent ? (
              <p>Does this look like the right flyer? If not, tap the image to change the flyer.</p>
            ) : (
              <p>Tap below to add the flyer.</p>
            )}

            <Card>
              <ImageUploadField
                placeholder="Add an event flyer..."
                defaultValue={selectedEvent?.img}
                onChange={(updatedImageValue) => {
                  track('Update Image');
                  setUpdatedImage(updatedImageValue);
                }}
              />
            </Card>

            <Button
              className="mt-3"
              roundness="rounded"
              disabled={isSubmittingFlyer || !isFormReady}
              onClick={handleSubmitFlyer}
            >
              {selectedEvent
                ? isFormDataTouched
                  ? 'Update Flyer Info'
                  : 'Confirm Details'
                : 'Add Flyer Details'}
            </Button>
          </>
        )
      ) : (
        <>
          <p className="mt-0">
            Sorry, if we missed your show! We're dedicated to making sure that we have upcoming
            Colorado drag shows listed, but that's hard to do on our own.
          </p>
          <p className="">
            Use the form below to submit your show's flyer, and we'll get it added as soon as we
            can.
          </p>

          <p>To get started, select the date of your show:</p>

          <div className={selectedDate ? 'd-flex gap-2 w-100' : 'd-grid gap-2'}>
            <div className="w-100">
              <DatePickerV2
                name="startDate"
                value={dateFieldValue}
                minDate={dayjs().format('YYYY-MM-DD')}
                onChange={(updatedDate) => {
                  setDateFieldValue(dayjs(updatedDate).format('YYYY-MM-DD'));
                }}
              />
            </div>
            {selectedDate !== dateFieldValue && (
              <Button
                roundness="rounded"
                disabled={!selectedDate ? !dateFieldValue : selectedDate === dateFieldValue}
                onClick={() => {
                  track('Update Selected Date', { date: selectedDate });
                  setSelectedDate(dateFieldValue);
                }}
                style={selectedDate ? { width: 100 } : undefined}
              >
                {!selectedDate ? 'Continue' : 'Update'}
              </Button>
            )}
          </div>

          {selectedDate && (
            <>
              <TitleToolbar text="Select your show:" size="sm" />

              {eventsAreReady ? (
                events?.length ? (
                  <>
                    <div style={{ margin: '0 -1rem' }}>
                      <F7List dividersIos outlineIos className="mt-2 mb-3">
                        {events.map((event) => (
                          <ListItem
                            key={`${event._id}-${selectedEventId === event._id}`}
                            checked={selectedEventId === event._id}
                            checkbox
                            title={event.title}
                            footer={event.timeDetails}
                            onClick={() => {
                              setSelectedEventId(event._id);
                            }}
                          />
                        ))}
                      </F7List>
                    </div>

                    <Button
                      disabled={!selectedEventId}
                      roundness="rounded"
                      onClick={() => {
                        track('Select Event', { selectedEventId });
                        setIsEventConfirmationStepVisible(true);
                      }}
                    >
                      Continue
                    </Button>

                    <Button
                      roundness="rounded"
                      variant="flat"
                      color="neutral"
                      onClick={() => {
                        track('Select No Event');
                        setSelectedEventId(undefined);
                        setIsEventConfirmationStepVisible(true);
                      }}
                    >
                      I don't see my show
                    </Button>
                  </>
                ) : (
                  <EmptyState
                    title="No shows added"
                    text="We don't have any shows added for the selected date"
                    button={{
                      text: 'Add Flyer',
                      onClick: () => {
                        track('Add Flyer (Empty State)');
                        setSelectedEventId(undefined);
                        setIsEventConfirmationStepVisible(true);
                      },
                    }}
                  />
                )
              ) : (
                <Skeleton height={200} />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export const AddPosterPopup: FC<{
  isVisible: boolean;
  onClose: () => void;
  series: Pick<Series, '_id'>;
  tagSlug: string;
}> = ({ isVisible, onClose, series, tagSlug }) => {
  return (
    <Popup isVisible={isVisible} onClose={onClose} height="full">
      <Popup.Content>
        {isVisible && <AddPosterPopupContent series={series} tagSlug={tagSlug} onClose={onClose} />}
      </Popup.Content>
    </Popup>
  );
};
